import React, { useEffect, useRef, useState } from 'react';
import { Box, Slide, TextField, Button, IconButton, Fade, Menu, MenuItem, Typography, Tooltip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import helpdeskIcon from '../Images/helpdesk.png';
import Cookies from 'js-cookie';
import DOMPurify from 'dompurify';

const CHAT_STORAGE_KEY = 'helpdeskChatOpen';
const SESSION_KEY = 'helpdeskSessionId';
const FLOW_ENDPOINT = 'https://flowise.hyperminds.tech/api/v1/prediction/fd61864b-d032-4a18-adf0-2a2c245dfcd7';

function getSessionId() {
  try {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = `sid_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return `sid_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }
}

function extractConnectorSelection(text) {
  const s = (text || '').trim();
  if (!s) return '';
  const rx = /\b(connector(?:s)?|connections?)\s*(?:for|type|of)?\s*([a-z0-9._\- ]{2,})$/i;
  const m = s.match(rx);
  if (m && m[2]) return m[2].trim();
  const slash = s.match(/^\/(?:connections?|connectors?)\s+(.+)$/i);
  if (slash && slash[1]) return slash[1].trim();
  return '';
}

async function callFlowise({
  question,
  humanInput,
  authToken,
  workflowSelection,
  workflowId,
  connectorSelection,
  connectorIdSelection,
  projectSelection,
  projectId
}) {
  const sessionId = getSessionId();
  const vars = {
    authToken,
    ...(workflowSelection ? { workflowSelection } : {}),
    ...(workflowId ? { workflowId } : {}),
    ...(connectorSelection ? { connectorSelection } : {}),
    ...(connectorIdSelection ? { connectorIdSelection } : {}),
    ...(projectSelection ? { projectSelection } : {}),
    ...(projectId ? { projectId } : {}),
    ...(humanInput?.feedback
      ? {
          workflowSelection: humanInput.feedback,
          connectorSelection: humanInput.feedback,
          connectorIdSelection: humanInput.feedback,
          projectSelection: humanInput.feedback,
        }
      : {}),
  };

  const body = {
    ...(humanInput ? {} : { question }),
    ...(humanInput ? { humanInput } : {}),
    overrideConfig: { sessionId, vars },
  };

  const resp = await fetch(FLOW_ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!resp.ok) throw new Error(`Status code ${resp.status}`);
  return resp.json();
}

function extractHumanInputAction(data) {
  if (data && data.humanInputAction && data.humanInputAction.id) {
    const conds = data.conditions || [];
    const expectedType =
      conds.find((c) => (c.type || '').toLowerCase() === 'proceed')?.type ||
      conds[0]?.type ||
      'proceed';
    return {
      id: data.humanInputAction.id,
      startNodeId: data.humanInputAction.startNodeId || null,
      expectedType,
      conditions: conds,
    };
  }

  const nodes = data?.agentFlowExecutedData || [];
  const hiNode = [...nodes].reverse().find(
    (n) =>
      n?.nodeId === 'humanInputAgentflow_0' ||
      (n?.nodeLabel || '').toLowerCase().includes('human') ||
      (n?.data?.id || '').toLowerCase().includes('humaninput')
  );

  const nested =
    hiNode?.data?.output?.humanInputAction || hiNode?.output?.humanInputAction;
  if (nested && nested.id) {
    const conds =
      hiNode?.data?.output?.conditions || hiNode?.output?.conditions || [];
    const expectedType =
      conds.find((c) => (c.type || '').toLowerCase() === 'proceed')?.type ||
      conds[0]?.type ||
      'proceed';

    return {
      id: nested.id,
      startNodeId: hiNode?.nodeId || hiNode?.data?.id || null,
      expectedType,
      conditions: conds,
    };
  }

  const text = (data?.text || '').toLowerCase();
  if (
    text.includes('please reply with the connector type') ||
    text.includes('please reply with the workflow id')
  ) {
    return {
      id: 'synthetic-human-input',
      startNodeId: 'humanInputAgentflow_0',
      expectedType: 'proceed',
      conditions: [{ type: 'proceed', label: 'Proceed' }],
    };
  }
  return null;
}

function stripLeadingEcho(botText, userText) {
  if (!botText) return botText || '';
  const bt = String(botText).replace(/\r\n/g, '\n');
  const ut = String(userText || '').trim();
  if (!ut) return bt;

  if (bt.trimStart().toLowerCase().startsWith(ut.toLowerCase())) {
    const after = bt.trimStart().slice(ut.length);
    return after.replace(/^(\s*<br\s*\/>|\s*\n)+/i, '').trimStart();
  }
  const htmlEcho = new RegExp(
    '^\\s*<p>\\s*' + ut.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*<\\/p>\\s*',
    'i'
  );
  if (htmlEcho.test(bt)) return bt.replace(htmlEcho, '').trimStart();
  return bt;
}

function collapseExcessBlankLines(s) {
  return String(s || '')
    .replace(/(?:\s*<br\s*\/>\s*){3,}/gi, '<br /><br />')
    .replace(/(?:\s*\n\s*){3,}/g, '\n\n')
    .trim();
}

const HelpdeskFloatingIcon = () => {
  const [openChat, setOpenChat] = useState(false);
  const chatRef = useRef(null);
  const iconRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);

  const [isDragging, setIsDragging] = useState(false);
  const hasDraggedRef = useRef(false);
  const getInitialPosition = () => {
    if (typeof window !== 'undefined') {
      return { right: 24, top: window.innerHeight - 58 - 24 };
    }
    return { right: 24, top: 24 };
  };
  const [position, setPosition] = useState(getInitialPosition);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const positionStartRef = useRef({ right: 0, top: 0 });

  const [awaitingHumanInput, setAwaitingHumanInput] = useState(false);
  const [humanFeedback, setHumanFeedback] = useState('');
  const [humanActionId, setHumanActionId] = useState(null);
  const [humanStartNodeId, setHumanStartNodeId] = useState(null);
  const [expectedActionType, setExpectedActionType] = useState('proceed');
  const [hitlConditions, setHitlConditions] = useState([]);

  const messagesRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const shouldAutoScrollRef = useRef(true);

  const [actionsAnchorEl, setActionsAnchorEl] = useState(null);
  const actionsOpen = Boolean(actionsAnchorEl);

  const computeIsAtBottom = () => {
    const el = messagesRef.current;
    if (!el) return true;
    const threshold = 24;
    return el.scrollHeight - el.scrollTop - el.clientHeight <= threshold;
  };

  const handleScroll = () => {
    const atBottom = computeIsAtBottom();
    setIsAtBottom(atBottom);
    shouldAutoScrollRef.current = atBottom;
  };

  const scrollToBottom = (behavior = 'smooth') => {
    const el = messagesRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  };

  useEffect(() => {
    const savedState = localStorage.getItem(CHAT_STORAGE_KEY);
    if (savedState === 'true') setOpenChat(true);

    const savedPosition = localStorage.getItem('helpdesk-icon-position');
    if (savedPosition) {
      try {
        const pos = JSON.parse(savedPosition);
        const iconSize = 58;
        const minY = 80;
        const maxY = window.innerHeight - iconSize - 24;
        
        const validatedPos = {
          right: Math.max(0, Math.min(window.innerWidth - iconSize, pos.right || 24)),
          top: Math.max(minY, Math.min(maxY, pos.top || window.innerHeight - iconSize - 24)),
        };
        setPosition(validatedPos);
      } catch (e) {
        console.error('Failed to parse saved position', e);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('helpdesk-icon-position', JSON.stringify(position));
  }, [position]);

  useEffect(() => {
    const handleResize = () => {
      const iconSize = 58;
      const minY = 80;
      const maxY = window.innerHeight - iconSize - 24;

      setPosition((prev) => ({
        right: Math.max(0, Math.min(window.innerWidth - iconSize, prev.right)),
        top: Math.max(minY, Math.min(maxY, prev.top)),
      }));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleDragStart = (clientX, clientY) => {
    if (openChat) return;
    setIsDragging(true);
    hasDraggedRef.current = false;
    dragStartRef.current = { x: clientX, y: clientY };
    positionStartRef.current = { ...position };
  };

  const handleDragMove = (clientX, clientY) => {
    if (!isDragging || openChat) return;

    const deltaX = dragStartRef.current.x - clientX;
    const deltaY = clientY - dragStartRef.current.y;

    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    if (distance > 5) {
      hasDraggedRef.current = true;
    }

    const newRight = positionStartRef.current.right + deltaX;
    const newTop = positionStartRef.current.top + deltaY;

    const iconSize = 58;
    const minY = 80;
    const maxY = window.innerHeight - iconSize - 24;
    const minRight = 0;
    const maxRight = window.innerWidth - iconSize;

    const constrainedRight = Math.max(minRight, Math.min(maxRight, newRight));
    const constrainedTop = Math.max(minY, Math.min(maxY, newTop));

    setPosition({ right: constrainedRight, top: constrainedTop });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setTimeout(() => {
      hasDraggedRef.current = false;
    }, 100);
  };

  const handleMouseDown = (e) => {
    if (openChat) return;
    e.preventDefault();
    handleDragStart(e.clientX, e.clientY);
  };

  const handleMouseMove = (e) => {
    handleDragMove(e.clientX, e.clientY);
  };

  const handleMouseUp = () => {
    handleDragEnd();
  };

  const handleTouchStart = (e) => {
    if (openChat) return;
    const touch = e.touches[0];
    handleDragStart(touch.clientX, touch.clientY);
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const touch = e.touches[0];
    handleDragMove(touch.clientX, touch.clientY);
  };

  const handleTouchEnd = () => {
    handleDragEnd();
  };

  useEffect(() => {
    if (!isDragging) return;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging]);

  useEffect(() => {
    localStorage.setItem(CHAT_STORAGE_KEY, openChat.toString());
    if (openChat) {
      setTimeout(() => {
        scrollToBottom('auto');
        setIsAtBottom(true);
        shouldAutoScrollRef.current = true;
      }, 0);
    }
  }, [openChat]);

  useEffect(() => {
    if (openChat && messages.length === 0) {
      setMessages([{ sender: 'bot', text: 'Hello, Shoot your query!' }]);
    }
  }, [openChat]);

  useEffect(() => {
    if (shouldAutoScrollRef.current) {
      scrollToBottom(messages.length <= 2 ? 'auto' : 'smooth');
    }
    setTimeout(() => setIsAtBottom(computeIsAtBottom()), 0);
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        chatRef.current &&
        !chatRef.current.contains(event.target) &&
        !event.target.closest('#helpdesk-floating-icon')
      ) {
        setOpenChat(false);
      }
    };
    if (openChat) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openChat]);

  const toggleChat = () => {
    if (!hasDraggedRef.current) {
      setOpenChat((prev) => !prev);
    }
  };

  const resetPosition = () => {
    if (typeof window !== 'undefined') {
      const defaultPosition = { right: 24, top: window.innerHeight - 58 - 24 };
      setPosition(defaultPosition);
      localStorage.setItem('helpdesk-icon-position', JSON.stringify(defaultPosition));
    }
  };

  const safeHtml = (html) => DOMPurify.sanitize(html || '');

  const pushBot = (rawHtml) => {
    const html = (rawHtml && rawHtml.trim()) ? rawHtml.replace(/\n/g, '<br />') : 'No response from chatbot.';
    setMessages((prev) => [...prev, { sender: 'bot', text: safeHtml(html) }]);
  };

  const sendMessage = async () => {
    if (isSending) return;
    const trimmed = (input || '').trim();
    if (!trimmed) return;

    const authToken = Cookies.get('authToken');
    const connectorSelection = extractConnectorSelection(trimmed);

    setInput('');
    setIsSending(true);

    setMessages((prev) => [...prev, { sender: 'user', text: safeHtml(trimmed) }]);
    shouldAutoScrollRef.current = true;

    try {
      const data = await callFlowise({
        question: trimmed,
        authToken,
        connectorSelection,
      });

      const cleaned = collapseExcessBlankLines(stripLeadingEcho(data.text, trimmed));
      pushBot(cleaned);

      const action = extractHumanInputAction(data);
      if (action && action.id) {
        setAwaitingHumanInput(true);
        setHumanActionId(action.id);
        setHumanStartNodeId(action.startNodeId || 'humanInputAgentflow_0');
        setExpectedActionType(action.expectedType || 'proceed');
        setHitlConditions(Array.isArray(action.conditions) ? action.conditions : []);
      } else {
        setAwaitingHumanInput(false);
        setHumanActionId(null);
        setHumanFeedback('');
        setHumanStartNodeId(null);
        setExpectedActionType('proceed');
        setHitlConditions([]);
      }
    } catch (err) {
      console.error('Flowise API call failed', err);
      pushBot('Error contacting Flowise.');
    } finally {
      setIsSending(false);
    }
  };

  const submitHumanInput = async (chosenType) => {
    if (!humanActionId) return;

    const authToken = Cookies.get('authToken');
    const type = chosenType || expectedActionType || 'proceed';
    const feedback = (humanFeedback || '').trim();

    shouldAutoScrollRef.current = true;

    try {
      const data = await callFlowise({
        humanInput: {
          id: humanActionId,
          type,
          feedback,
          startNodeId: humanStartNodeId || 'humanInputAgentflow_0',
        },
        authToken,
        workflowSelection: feedback || undefined,
        connectorSelection: feedback || undefined,
        connectorIdSelection: feedback || undefined,
      });

      const cleaned = collapseExcessBlankLines(stripLeadingEcho(data.text, feedback));
      pushBot(cleaned);

      setAwaitingHumanInput(false);
      setHumanActionId(null);
      setHumanFeedback('');
      setHumanStartNodeId(null);
      setExpectedActionType('proceed');
      setHitlConditions([]);
    } catch (err) {
      console.error('Flowise resume failed', err);
      pushBot('Error resuming after approval.');
    }
  };

  const openActions = (e) => setActionsAnchorEl(e.currentTarget);
  const closeActions = () => setActionsAnchorEl(null);
  const pickAction = (text) => {
    setInput(text);
    closeActions();
  };

  return (
    <>
      <Box
        id="helpdesk-floating-icon"
        ref={iconRef}
        sx={{
          position: 'fixed',
          right: position.right,
          top: position.top,
          width: 58,
          height: 58,
          borderRadius: '50%',
          boxShadow: '0px 4px 12.8px 0px #00000040',
          background: 'linear-gradient(135deg, #166534 0%, #15803d 50%, #16a34a 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: openChat ? 'pointer' : isDragging ? 'grabbing' : 'grab',
          zIndex: 1300,
          transition: isDragging ? 'none' : 'transform 0.3s ease, background 0.3s ease',
          userSelect: 'none',
          touchAction: 'none',
          '&:hover': {
            transform: openChat || isDragging ? 'none' : 'scale(1.05)',
            background: isDragging
              ? 'linear-gradient(135deg, #3B9562 0%,rgb(56, 123, 85) 50%, #16a34a 100%)'
              : 'linear-gradient(135deg, #3B9562 0%,rgb(56, 123, 85) 50%, #16a34a 100%)',
          },
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onClick={toggleChat}
      >
        <Box component="img" src={helpdeskIcon} alt="Helpdesk" sx={{ width: 24, height: 24 }} />
      </Box>

      <Slide direction="up" in={openChat} mountOnEnter unmountOnExit>
        <Box
          ref={chatRef}
          sx={{
            position: 'fixed',
            bottom: 90,
            right: 24,
            zIndex: 1299,
            width: 380,
            height: 520,
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: 4,
            backgroundColor: '#fff',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 1.5,
              borderBottom: '1px solid #e5e7eb',
              bgcolor: '#f9fafb',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#1f2937',
              }}
            >
              Help & Support
            </Typography>
            <Tooltip title="Reset icon position">
              <IconButton
                size="small"
                onClick={resetPosition}
                sx={{
                  color: '#6b7280',
                  '&:hover': {
                    color: '#0056D2',
                    bgcolor: 'rgba(0, 86, 210, 0.1)',
                  },
                }}
              >
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Box
            ref={messagesRef}
            onScroll={handleScroll}
            sx={{ flex: 1, p: 2, overflowY: 'auto', position: 'relative' }}
          >
            {messages.map((m, idx) => (
              <Box key={idx} sx={{ textAlign: m.sender === 'user' ? 'right' : 'left', mb: 1 }}>
                <Box
                  sx={{
                    display: 'inline-block',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: m.sender === 'user' ? '#0056D2' : '#e0e0e0',
                    color: m.sender === 'user' ? '#fff' : '#000',
                    wordBreak: 'break-word',
                    maxWidth: '100%',
                  }}
                  dangerouslySetInnerHTML={{ __html: m.text }}
                />
              </Box>
            ))}
          </Box>

          <Fade in={openChat && !isAtBottom}>
            <IconButton
              onClick={() => scrollToBottom('smooth')}
              sx={{
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
                bottom: 72,
                zIndex: 2,
                bgcolor: '#ffffff',
                border: '1px solid #e5e7eb',
                boxShadow: '0 6px 14px rgba(17,24,39,0.15)',
                '&:hover': { bgcolor: '#f9fafb' },
              }}
              size="large"
              aria-label="Scroll to bottom"
            >
              <KeyboardArrowDownIcon />
            </IconButton>
          </Fade>

          <Fade in={isSending}>
            <Box sx={{ px: 2, pb: 1, color: '#6b7280', fontSize: 12 }}>
              Bot is typing…
            </Box>
          </Fade>

          {awaitingHumanInput && (
            <Box sx={{ p: 1, borderTop: '1px solid #eee', display: 'flex', gap: 1, flexDirection: 'column' }}>
              <Box sx={{ fontSize: 12, color: '#666' }}>
                The assistant needs additional info. Please provide it below.
              </Box>
              <TextField
                multiline
                minRows={2}
                placeholder='Provide the required input here...'
                value={humanFeedback}
                onChange={(e) => setHumanFeedback(e.target.value)}
              />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                {hitlConditions && hitlConditions.length > 0 ? (
                  hitlConditions.map((c) => (
                    <Button key={c.type} variant="contained" onClick={() => submitHumanInput(c.type)}>
                      {c.label || c.type}
                    </Button>
                  ))
                ) : (
                  <Button variant="contained" onClick={() => submitHumanInput('proceed')}>
                    Proceed
                  </Button>
                )}
              </Box>
            </Box>
          )}

          <Box sx={{ p: 1, display: 'flex', alignItems: 'center' }}>
            <TextField
              fullWidth
              size="small"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              disabled={isSending}
            />
            <Button onClick={sendMessage} sx={{ ml: 1 }} variant="contained" disabled={isSending}>
              Send
            </Button>
            <Button
              onClick={openActions}
              sx={{ ml: 1 }}
              variant="outlined"
              disabled={isSending}
            >
              Actions ▾
            </Button>
            <Menu
              anchorEl={actionsAnchorEl}
              open={actionsOpen}
              onClose={closeActions}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
              <MenuItem onClick={() => pickAction('show my workflows')}>List Workflows</MenuItem>
              <MenuItem onClick={() => pickAction('run workflow')}>Run Workflow</MenuItem>
              <MenuItem onClick={() => pickAction('show my connections')}>List Connections</MenuItem>
              <MenuItem onClick={() => pickAction('show my projects')}>List Projects</MenuItem>
              <MenuItem onClick={() => pickAction('open help center')}>Open Help</MenuItem>
              <MenuItem onClick={() => pickAction('contact support')}>Contact Support</MenuItem>
            </Menu>
          </Box>
        </Box>
      </Slide>
    </>
  );
};

export default HelpdeskFloatingIcon;
